import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { IconButton, Tooltip } from "@mui/material";
import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PageHeaderTitle from "../PageHeaderTitle";
import { Alert, Box, Button, Chip, LinearProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import type { ContentReviewItem } from "../../services/contentReviewService";
import { counted, displayCount, unavailable, type SocialsCount } from "../../lib/socialsCounts";
import SocialsListState from "./SocialsListState";
import type { SocialsDecisionState } from "./useSocialsDecisions";
import ReelsTab from "./reels/ReelsTab";
import BehindTheScenesDialog, { type HealthRow } from "./BehindTheScenesDialog";
import ReviewScreen from "./reels/ReviewScreen";
import { groupReelVersions } from "../../lib/reelVersionChain";
import EngageTab, { type EngageState, type EngageAction } from "./engage/EngageTab";
import OverviewTab from "./overview/OverviewTab";
import ScheduleDialog from "./ScheduleDialog";
import { stageOf } from "../../lib/socialsStages";
import { reelName, ageLabel, isStale } from "../../lib/reelIdentity";
import { unknown } from "../../lib/socialsCounts";
import type {
  ScheduledPost,
  PublishedPost,
  ProposedSlot,
} from "../../lib/postingCalendar";
import { groupByPost, engageQueueState, type EngageLead } from "../../lib/engageGrouping";
import type { ContentGridVerdict } from "../../services/contentReviewService";

/**
 * Ch94 · SOCIALS — the shell.
 *
 * R23, Grant's words: *"Content is better names 'Socials' really, right? yes
 * they are all part of the same machine -- responding on reddit and doing the
 * Insta actions have similar benefit to consistently posting"*. Posting reels,
 * replying on Reddit and Instagram actions are ONE machine and stay together.
 *
 * 🔴 THE HEADER AND THE TAB BAR NEVER SWAP PLACES. Measured live by the Ch94
 * Director: today the header and source chips change vertical position between
 * sub-tabs, so the control you just used moves out from under your thumb. Here
 * the header and tab bar are rendered ONCE, outside the switch, and only the
 * panel below them changes. That is the whole mechanism — it cannot regress by
 * someone editing one branch, because there is only one.
 *
 * WEEKLY GOALS ARE PLACEHOLDERS AND SAY SO. 5 posts / 10 replies were invented
 * by the Ch94 Director for the mock; they are not Grant's numbers. They are
 * named constants here and the UI labels them as a starting point, because a
 * target presented as his when he never set it is a quiet lie that he would
 * then be measured against.
 */

/** Not Grant's numbers. A starting point until he sets them. */
export const DEFAULT_WEEKLY_GOALS = { posts: 5, replies: 10 };

export type SocialsTabKey = "overview" | "reels" | "engage";

interface Props {
  renderFootage?: (item:ContentReviewItem,seek:(seconds:number)=>void,reviewBusy?:boolean)=>React.ReactNode;
  footageBlocked?: (item:ContentReviewItem)=>boolean;
  headerMeta?: React.ReactNode;
  connectedSchedule?: boolean;
  items: ContentReviewItem[] | null;
  loading?: boolean;
  error?: string | null;
  decisionStates?: Record<string, SocialsDecisionState>;
  onRetryDecision?: (item: ContentReviewItem) => void;
  totalKnown: number | null;
  /** Reddit conversations waiting. Unavailable until that desk answers. */
  repliesCount?: SocialsCount;
  health?: HealthRow[];
  initialTab?: SocialsTabKey;
  onTabChange?: (tab: SocialsTabKey) => void;
  onHealthOpen?: () => void;
  /** Optional observer — the screen itself is owned here. */
  onReview?: (item: ContentReviewItem) => void;
  /** Phase 4 wires scheduling; absent means the control is not offered. */
  onSchedule?: (item: ContentReviewItem) => void;
  onMarkPosted?: (item: ContentReviewItem) => void;
  scheduleBlockedReason?: string;
  onVerdict?: (
    item: ContentReviewItem,
    verdict: ContentGridVerdict,
    note?: string
  ) => void;
  /** Engage. Absent leads ⇒ the tab says so rather than showing an empty list. */
  engageLeads?: EngageLead[];
  initialLead?: string | null;
  engageLoading?: boolean;
  engageError?: string | null;
  engageBusyId?: string | null;
  engageCounts?: Partial<Record<EngageState, number>>;
  onEngageAction?: (action: EngageAction) => void;
  onTurnIntoLead?: (lead: EngageLead) => void;
  /** Phase 4 supplies these; none exist yet. */
  scheduled?: ScheduledPost[];
  /** `null` = content_posts not read. NOT "nothing posted". */
  posted?: PublishedPost[] | null;
  /** Instagram posts we cannot match to a reel. Unknown until read. */
  unmatched?: SocialsCount;
  onScheduleSlots?: (slots: ProposedSlot[]) => void;
  /** This week's real numbers for the header bars. Unknown until read. */
  postsThisWeek?: SocialsCount;
  repliesThisWeek?: SocialsCount;
  onRefresh?: () => void;
  onRevisionQueued?: () => void;
}

/**
 * A module-level constant, NOT a `= []` default parameter.
 *
 * A default array literal is a NEW ARRAY on every render, which invalidates any
 * memo downstream that depends on it — half of why ScheduleDialog's date field
 * reset on every keystroke.
 */
const EMPTY_SLOTS: ScheduledPost[] = [];

/** A bar can only fill from a real reading; unknown shows an empty track. */
const goalPercent = (count: SocialsCount, goal: number): number =>
  count.kind === "counted" && goal > 0
    ? Math.min(100, (count.value / goal) * 100)
    : 0;

const TAB_ORDER: SocialsTabKey[] = ["overview", "reels", "engage"];
const TAB_LABELS: Record<SocialsTabKey, string> = {
  overview: "Overview",
  reels: "Reels",
  engage: "Engage",
};

const SocialsPage: React.FC<Props> = ({
  headerMeta,
  items,
  connectedSchedule = false,
  loading, error, decisionStates = {}, onRetryDecision, renderFootage, footageBlocked,
  totalKnown,
  repliesCount,
  health = [],
  initialTab = "overview", onTabChange, onHealthOpen,
  onReview,
  onSchedule, onMarkPosted, scheduleBlockedReason,
  onVerdict,
  engageLeads, initialLead, engageLoading, engageError, engageBusyId,
  engageCounts,
  onEngageAction,
  onTurnIntoLead,
  scheduled = EMPTY_SLOTS,
  posted = null,
  unmatched,
  onScheduleSlots,
  postsThisWeek,
  repliesThisWeek,
  onRefresh, onRevisionQueued,
}) => {
  // Unknown until something reads content_posts / the Reddit ledger. An unread
  // number shows an em dash and an empty bar — never a 0 that looks like effort
  // measured and found wanting.
  const postsBar = postsThisWeek ?? unknown();
  const repliesBar = repliesThisWeek ?? unknown();
  const [tab, setTab] = React.useState<SocialsTabKey>(initialTab);
  React.useEffect(() => { onTabChange?.(tab); }, [tab, onTabChange]);
  const [btsOpen, setBtsOpen] = React.useState(false);
  // The card under review, held by ID rather than index: a refresh or a filter
  // change must not silently move the decision onto a different reel.
  const [reviewingId, setReviewingId] = React.useState<string | null>(null);
  const [selectedLead, setSelectedLead] = React.useState(initialLead);
  const [engageState, setEngageState] = React.useState<EngageState>("needs_reply");
  /** The reel whose time is being picked. Null = the dialog is closed. */
  const [schedulingId, setSchedulingId] = React.useState<string | null>(null);

  /*
   * 🔴 REVIEW WALKS THE SAME LIST THE GRID SHOWS — the chain HEADS, not the raw
   * items. Binding rule: "One card per video."
   *
   * Caught on screen: the grid showed 15 tiles while the review screen counted
   * "1 of 16", because it was walking raw items. Next would then have carried
   * Grant onto a superseded version of a reel he had just decided about, and
   * the count under his thumb would have disagreed with the count above it.
   */
  const reviewable = React.useMemo(
    () => groupReelVersions(items ?? []).map((group) => group.head as ContentReviewItem),
    [items]
  );

  // Keep the selected original open when its child arrives and becomes the grid head.
  const reviewItems = React.useMemo(() => {
    const selected = items?.find(item => item.id === reviewingId);
    return selected && !reviewable.some(item => item.id === selected.id) ? [...reviewable, selected] : reviewable;
  }, [items, reviewable, reviewingId]);

  const reviewIndex = React.useMemo(
    () => reviewItems.findIndex((candidate) => candidate.id === reviewingId),
    [reviewItems, reviewingId]
  );

  const openReview = React.useCallback(
    (item: ContentReviewItem) => {
      setReviewingId(item.id);
      onReview?.(item);
    },
    [onReview]
  );

  const reelsCount = items === null ? unknown() : counted(reviewable.length);

  /*
   * 🔴 BOTH TAB COUNTS PROMISE WHAT HE WILL ACTUALLY FIND.
   *
   * Caught on screen twice, the same way: Reels badged 16 raw items over 15
   * tiles, and Engage badged 6 raw leads over 3 rows — because versions and
   * repeats both collapse. A badge that counts rows the list will not show is
   * a number he cannot reconcile with the screen under it, and it inflates the
   * pile: the live Engage queue reads "99 to reply to" for the same reason.
   *
   * An explicit `repliesCount` still wins — the server may know about work
   * beyond the page we loaded.
   */
  /*
   * Candidate reel labels come from the review queue. In connected mode Overview
   * reads the authoritative approved-unscheduled IDs from the slot API, which
   * requires both approved status and the applied P command.
   */
  const approvedWaiting = React.useMemo(
    () =>
      reviewable
        .filter((item) => stageOf(item).stage === "approved")
        .map((item) => ({
          reviewId: item.id,
          versionKey: JSON.stringify([item.updated_at, item.revision_number, item.command, item.post_kit]),
          subject: reelName(item).text,
          template: item.template,
          about: reelName(item).source === "none" ? null : reelName(item).text,
        })),
    [reviewable]
  );

  /** The next few reels waiting, for the needs-you tile. */
  const reviewQueue = React.useMemo(
    () =>
      reviewable
        .filter((item) => stageOf(item).stage === "to_review")
        .slice(0, 3)
        .map((item) => ({
          id: item.id,
          name: item.property_name?.trim() || item.scenes?.find(scene => scene.caption?.trim())?.caption?.trim() || reelName(item).text,
          frameSheetAvailable: item.frame_sheet_available,
          template: item.template,
          age: ageLabel(item.updated_at),
          stale: isStale(item.updated_at),
        })),
    [reviewable]
  );

  /** The top conversations, for the needs-you tile. */
  const threads = React.useMemo(
    () =>
      groupByPost(engageLeads ?? []).filter(group => engageQueueState(group.lead) === "needs_reply")
        .slice(0, 2)
        .map((group) => ({
          id: group.lead.id,
          title: String(
            (group.lead.raw_payload as { title?: unknown } | null)?.title ??
              group.lead.body_preview ??
              "A Reddit post"
          ),
          source: String(
            (group.lead.raw_payload as { subreddit?: unknown } | null)?.subreddit ?? "Reddit"
          ),
          hot: group.lead.verdict === "HOT",
        })),
    [engageLeads]
  );

  const engageCount =
    repliesCount ??
    (engageLeads ? counted(groupByPost(engageLeads).length) : unavailable("not set up yet"));

  // Amber/red ONLY when Grant must act — anything else stays quiet.
  const needsAction = health.some((row) => row.tone === "act");

  const tabCount = (key: SocialsTabKey): string | null => {
    if (key === "reels") return displayCount(reelsCount).text;
    if (key === "engage") {
      const shown = displayCount(engageCount);
      // An unavailable source renders words elsewhere; on a tab that would be
      // absurdly long, so the tab simply carries no number rather than a "0"
      // that would claim there is nothing to do.
      return shown.isNumber ? shown.text : null;
    }
    return null;
  };

  return (
    // The app shell owns page width and gutters, as it does for Leads.
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
      {/*
        HEADER — rendered once, never inside the tab switch.
      */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
        gap={2}
        flexWrap="nowrap"
        data-testid="socials-header"
      >
        <PageHeaderTitle
          icon={<ContentCopyIcon />}
          title="Socials"
          titleVariant="h5"
        />

        {/*
          THE MOCK'S HEADER SHAPE: two small progress bars, a machine-state chip
          and Refresh. Rebuilt after Grant said the page "didn't look complete" —
          I had a sentence and one large button where he approved a compact
          status strip, and the machine-state chip was a thing he liked.

          The honesty stays: the goals are still labelled as NOT HIS, because an
          agent invented 5/10 for the mock. The mock's shape, my wording.
        */}
        <Stack direction="row" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Chip
            size="small"
            variant="outlined"
            onClick={() => { setBtsOpen(true); onHealthOpen?.(); }}
            data-testid="socials-health-button"
            label={needsAction ? "Something needs you" : "Behind the scenes"}
            sx={{
              color: needsAction ? "warning.main" : "text.secondary",
              borderColor: needsAction ? "warning.main" : "divider",
            }}
          />
          {onRefresh ? (
            <Tooltip title="Refresh"><IconButton aria-label="Refresh" onClick={onRefresh} data-testid="socials-refresh" sx={{ width: 40, height: 40 }}><RefreshOutlined sx={{ fontSize: 20 }} /></IconButton></Tooltip>
          ) : null}
        </Stack>
      </Stack>

      {/* TAB BAR — also rendered once, in the same place, always. */}
      <Tabs
        value={tab}
        onChange={(_event, next: SocialsTabKey) => setTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        data-testid="socials-tabs"
      >
        {TAB_ORDER.map((key) => {
          const count = tabCount(key);
          return (
            <Tab
              key={key}
              value={key}
              label={
                <Stack direction="row" gap={0.75} alignItems="center">
                  <span>{TAB_LABELS[key]}</span>
                  {count ? <Chip size="small" label={count} /> : null}
                </Stack>
              }
            />
          );
        })}
      </Tabs>
      {headerMeta}
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ minWidth: 92 }} data-testid="goal-posts">
            <Typography variant="caption" color="text.secondary">
              Posts {displayCount(postsBar).text} / {DEFAULT_WEEKLY_GOALS.posts}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={goalPercent(postsBar, DEFAULT_WEEKLY_GOALS.posts)}
              sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
            />
          </Box>
          <Box sx={{ minWidth: 92 }} data-testid="goal-replies">
            <Typography variant="caption" color="text.secondary">
              Replies {displayCount(repliesBar).text} / {DEFAULT_WEEKLY_GOALS.replies}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={goalPercent(repliesBar, DEFAULT_WEEKLY_GOALS.replies)}
              sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
            />
          </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" data-testid="goals-caveat">
          Goals to aim at — not yours yet: {DEFAULT_WEEKLY_GOALS.posts} posts ·{" "}
          {DEFAULT_WEEKLY_GOALS.replies} replies a week
        </Typography>
      </Stack>

      </Stack>

      <Box data-testid={`socials-panel-${tab}`}>
        {Object.entries(decisionStates).map(([id, state]) => (
          <Alert key={id} severity={state.kind === "error" ? "error" : state.kind === "unknown" ? "warning" : "info"}
            data-testid={`decision-${state.kind}`} sx={{ mb: 1 }}
            action={state.kind === "unknown" && onRetryDecision ? <Button onClick={() => {
              const item = items?.find(candidate => candidate.id === id) ?? {id} as ContentReviewItem; onRetryDecision(item);
            }}>Retry decision safely</Button> : undefined}>
            {reelName(items?.find(candidate => candidate.id === id) ?? {id} as ContentReviewItem).text}: {state.message}
          </Alert>
        ))}
        {tab !== "engage" && (loading || error || items === null) ? <SocialsListState loading={loading} error={error} items={items}
          onRetry={onRefresh} testIdPrefix={tab} emptyMessage="Nothing here right now."
          unreadMessage="We couldn't read this yet. Try refreshing." /> : null}
        {tab === "reels" && !loading && !error && items !== null ? (
          <ReelsTab
            items={items}
            totalKnown={totalKnown}
            unreadStages={[...(!onScheduleSlots ? ["scheduled" as const] : []), ...(items?.some(item => item.status === "approved" && item.posted_platforms === undefined) ? ["posted" as const] : [])]}
            busyIds={Object.keys(decisionStates).filter(id => ["busy", "unknown"].includes(decisionStates[id].kind))}
            onReview={openReview}
            onQuickVerdict={
              onVerdict ? (item, verdict) => onVerdict(item, verdict) : undefined
            }
          />
        ) : null}

        {tab === "overview" && !loading && !error && items !== null ? (
          <OverviewTab
            connected={connectedSchedule}
            counts={{
              toReview: counted(
                reviewable.filter((item) => stageOf(item).stage === "to_review").length
              ),
              approvedWaiting: counted(approvedWaiting.length),
              conversations: engageLeads ? counted(groupByPost(engageLeads).filter(group => engageQueueState(group.lead) === "needs_reply").length) : unknown(),
            }}
            scheduled={scheduled}
            posted={posted}
            readyToSchedule={approvedWaiting}
            unmatched={unmatched ?? unknown()}
            weeklyGoal={DEFAULT_WEEKLY_GOALS.posts}
            onSchedule={onScheduleSlots}
            onScheduleOne={onScheduleSlots ? (reviewId) => setSchedulingId(reviewId) : undefined}
            onStartReviewing={() => {
              setTab("reels");
              const first = reviewable.find((i) => stageOf(i).stage === "to_review");
              if (first) setReviewingId(first.id);
            }}
            reviewQueue={reviewQueue}
            threads={threads}
            onOpenReels={() => setTab("reels")}
            onOpenEngage={() => setTab("engage")}
            onOpenThread={id => { setSelectedLead(id); setTab("engage"); }}
          />
        ) : null}

        {tab === "engage" ? (
          <EngageTab
            leads={engageLeads ?? null}
            initialLead={selectedLead}
            loading={engageLoading ?? loading} error={engageError === undefined ? error : engageError} onRetry={onRefresh} busyId={engageBusyId}
            state={engageState}
            onStateChange={state => { setSelectedLead(null); setEngageState(state); }}
            counts={engageCounts}
            onAction={(action) => onEngageAction?.(action)}
            onTurnIntoLead={onTurnIntoLead}
          />
        ) : null}
      </Box>

      <BehindTheScenesDialog
        open={btsOpen}
        onClose={() => setBtsOpen(false)}
        rows={health}
      />

      <ScheduleDialog
        open={Boolean(schedulingId)}
        subject={
          reviewable.find((item) => item.id === schedulingId)
            ? reelName(reviewable.find((item) => item.id === schedulingId)!).text
            : ""
        }
        scheduled={scheduled}
        onCancel={() => setSchedulingId(null)}
        onConfirm={
          onScheduleSlots && schedulingId
            ? (when) => {
                const item = reviewable.find((candidate) => candidate.id === schedulingId);
                if (item) {
                  onScheduleSlots([
                    {
                      reel: {
                        reviewId: item.id,
                        subject: reelName(item).text,
                        template: item.template,
                        about: reelName(item).text,
                      },
                      dayMs: when.getTime(),
                    },
                  ]);
                }
                setSchedulingId(null);
              }
            : undefined
        }
      />

      <ReviewScreen renderFootage={renderFootage} footageBlocked={footageBlocked}
        open={reviewIndex >= 0}
        items={reviewItems}
        relatedItems={items ?? []}
        onOpenRelated={openReview}
        onRevisionQueued={onRevisionQueued}
        index={reviewIndex < 0 ? 0 : reviewIndex}
        onClose={() => setReviewingId(null)}
        onMove={(next) => setReviewingId(reviewItems[next]?.id ?? null)}
        onVerdict={(item, verdict, note) => {
          onVerdict?.(item, verdict, note);
        }}
        onSchedule={onSchedule}
        onMarkPosted={onMarkPosted}
        scheduleBlockedReason={scheduleBlockedReason}
        busy={Boolean(reviewingId && ["busy", "unknown"].includes(decisionStates[reviewingId]?.kind))}
        decisionMessage={reviewingId ? decisionStates[reviewingId]?.message : undefined}
        onRetry={reviewingId && decisionStates[reviewingId]?.kind === "unknown" && onRetryDecision
          ? () => { const item = items?.find(i => i.id === reviewingId); if (item) onRetryDecision(item); } : undefined}
      />
    </Stack>
  );
};

export default SocialsPage;
